import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeesAPI } from '../../services/api';

const COUNTRY_CODES = [
  { code: '+1',   label: '+1  US/CA' },
  { code: '+91',  label: '+91  India' },
  { code: '+44',  label: '+44  UK' },
  { code: '+61',  label: '+61  Australia' },
  { code: '+971', label: '+971 UAE' },
  { code: '+65',  label: '+65  Singapore' },
  { code: '+86',  label: '+86  China' },
  { code: '+49',  label: '+49  Germany' },
  { code: '+33',  label: '+33  France' },
  { code: '+81',  label: '+81  Japan' },
];

const INIT = {
  first_name: '', last_name: '', date_of_birth: '',
  email: '', country_code: '+1', phone: '',
  current_address: '', category: '',
  uscis_number: '', ead_expiry_date: '',
};

export default function CreateEmployee() {
  const navigate = useNavigate();
  const [form,     setForm]     = useState(INIT);
  const [errors,   setErrors]   = useState({});
  const [apiError, setApiError] = useState('');
  const [loading,  setLoading]  = useState(false);

  function change(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.first_name.trim())  e.first_name = 'First name is required.';
    if (!form.last_name.trim())   e.last_name  = 'Last name is required.';
    if (!form.date_of_birth)      e.date_of_birth = 'Date of birth is required.';
    if (!form.email.trim())       e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
    const digits = form.phone.replace(/\D/g, '');
    if (!form.phone.trim())       e.phone = 'Phone number is required.';
    else if (digits.length !== 10) e.phone = 'Phone must be exactly 10 digits.';
    if (!form.current_address.trim()) e.current_address = 'Current address is required.';
    if (!form.category)           e.category = 'Please select a category.';
    if (!form.uscis_number.trim()) e.uscis_number = 'USCIS number is required.';
    if (!form.ead_expiry_date)    e.ead_expiry_date = 'EAD expiry date is required.';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await employeesAPI.create({
        ...form,
        phone: form.phone.replace(/\D/g, ''), // send digits only
      });
      navigate(`/employees/${res.data.data.employee_id}/documents`);
    } catch (err) {
      setApiError(err.response?.data?.message || 'Failed to create employee.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 text-lg">Employee Information</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            All fields are required. You will be redirected to upload documents after saving.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-6 space-y-5">

          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First Name" error={errors.first_name}>
              <input type="text" value={form.first_name}
                onChange={(e) => change('first_name', e.target.value)}
                placeholder="John" className={cls(errors.first_name)} />
            </Field>
            <Field label="Last Name" error={errors.last_name}>
              <input type="text" value={form.last_name}
                onChange={(e) => change('last_name', e.target.value)}
                placeholder="Doe" className={cls(errors.last_name)} />
            </Field>
          </div>

          {/* DOB + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date of Birth" error={errors.date_of_birth}>
              <input type="date" value={form.date_of_birth}
                onChange={(e) => change('date_of_birth', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={cls(errors.date_of_birth)} />
            </Field>
            <Field label="Email Address" error={errors.email}>
              <input type="email" value={form.email}
                onChange={(e) => change('email', e.target.value)}
                placeholder="john@example.com" className={cls(errors.email)} />
            </Field>
          </div>

          {/* Phone with country code */}
          <Field label="Phone Number" error={errors.phone}>
            <div className="flex gap-2">
              <select
                value={form.country_code}
                onChange={(e) => change('country_code', e.target.value)}
                className="border border-slate-300 rounded-lg px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shrink-0"
              >
                {COUNTRY_CODES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => {
                  // Allow only digits, spaces, dashes
                  const val = e.target.value.replace(/[^\d\s\-]/g, '');
                  change('phone', val);
                }}
                placeholder="10-digit number"
                maxLength={14}
                className={`flex-1 ${cls(errors.phone)}`}
              />
            </div>
            {form.phone && (
              <p className={`text-xs mt-1 ${form.phone.replace(/\D/g,'').length === 10 ? 'text-green-600' : 'text-slate-400'}`}>
                {form.phone.replace(/\D/g,'').length}/10 digits
              </p>
            )}
          </Field>

          {/* Current Address */}
          <Field label="Current Address" error={errors.current_address}>
            <textarea
              value={form.current_address}
              onChange={(e) => change('current_address', e.target.value)}
              placeholder="Street, City, State, ZIP"
              rows={2}
              className={`${cls(errors.current_address)} resize-none`}
            />
          </Field>

          {/* Category */}
          <Field label="Visa / Work Category" error={errors.category}>
            <select value={form.category}
              onChange={(e) => change('category', e.target.value)}
              className={cls(errors.category)}>
              <option value="">— Select category —</option>
              <option value="OPT">OPT (Optional Practical Training)</option>
              <option value="STEM_OPT">STEM OPT (STEM Extension)</option>
              <option value="CPT">CPT (Curricular Practical Training)</option>
              <option value="H1B">H-1B (Specialty Occupation)</option>
              <option value="H4_EAD">H-4 EAD (Dependent EAD)</option>
              <option value="GC">GC (Green Card)</option>
              <option value="CITIZENSHIP">Citizenship</option>
            </select>
          </Field>

          {/* USCIS + EAD Expiry */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="USCIS Number" error={errors.uscis_number}>
              <input type="text" value={form.uscis_number}
                onChange={(e) => change('uscis_number', e.target.value)}
                placeholder="MSC2112345678" className={cls(errors.uscis_number)} />
            </Field>
            <Field label="EAD Expiry Date" error={errors.ead_expiry_date}>
              <input type="date" value={form.ead_expiry_date}
                onChange={(e) => change('ead_expiry_date', e.target.value)}
                className={cls(errors.ead_expiry_date)} />
            </Field>
          </div>

          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
              {apiError}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => navigate('/employees')}
              className="flex-1 border border-slate-300 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Saving…</>
                : 'Save & Upload Documents →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} <span className="text-red-500">*</span>
      </label>
      {children}
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
}

function cls(error) {
  return `w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
    ${error ? 'border-red-400 bg-red-50' : 'border-slate-300'}`;
}
